import React, { useState, useEffect, useRef } from "react";
import { navigate } from "gatsby";
import {
  hoverClick,
  hoverClickLeft,
  hoverBig,
  fontColorDark,
  responsivePaddingFloorplanImage,
} from "./shared.module.scss";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import FloorplanNameTitle from "./tiny_components/floorPlanTitle";
import FloorplanLink from "./tiny_components/floorPlanLink";
import FloorplanDeal from "./tiny_components/floorplanDeal";
import PriceDisplay from "./tiny_components/priceDisplay";
import ApplyButton from "./applyButton";
import CrossSellFloorplanCard from "./CrossSellFloorplanCard";

const findPrice = (pricesAv, prices, uname, skiptoMaxMarket = false) => {
  // Initialize result
  let res = 0;

  // Normalize the uname and handle special cases
  if (uname == "The Conroy") {
    uname = "Parc";
  }

  // Not just for sapphire
  const sapphirePlans = [
    "Quartz+",
    "Emerald+",
    "Jade+",
    "Onyx+",
    "Kipling+",
    "Chatham+",
    "Granada+",
    "Avalon+",
    "Alafia River+",
    "Blue Spring+",
    "Mills+",
    "Central+",
    "Corrine+",
    "Park+",
  ];

  if (uname.endsWith("+") && !sapphirePlans.includes(uname)) {
    uname = uname.slice(0, -1);
  }
  const searchName = uname.toLowerCase().replace("the ", "");

  // Define a helper function to find the price in a given price list
  const getPrice = (priceList) => {
    // First we have to check if an exact name matches (issue caused by plus floorplans)
    const exactMatchKey = Object.keys(priceList).find(
      (key) => key.toLowerCase() === searchName,
    );
    if (exactMatchKey) {
      return parseInt(priceList[exactMatchKey]);
    }

    // Now we look for includes if an exact match was not found
    const matchingKey = Object.keys(priceList).find((key) => {
      const formattedKey = key.toLowerCase();
      return formattedKey.includes(searchName);
    });
    if (matchingKey) {
      return parseInt(priceList[matchingKey]);
    }

    // Nothing found
    return 0; // Return 0 if no matching key is found
  };

  // Try to find the price in pricesAv first
  res = getPrice(pricesAv);

  // If the result is 0, try to find it in the prices list
  if (res === 0 || skiptoMaxMarket) {
    res = getPrice(prices);
  }

  return res;
};

const isFloorPlanOnSale = (fpdeals, fpname, cname="") => {
  //console.log("fpdeals", fpdeals);
  //console.log("fpname", fpname);
  //console.log("cname", cname);

  if (fpname === "The Gaston & The Grand") {
    // name hacks
    fpname = "The Grand";
  }

  if (fpdeals) {
    for (let element of fpdeals) {
      if (element.includes(" Plus")) {
        element = element.replace(" Plus", "+");
      }
      //console.log("element", element);
      // Extract the floor plan name from the deal
      const floorPlanName = element.split(" - ").pop().trim();

      if (
        floorPlanName === fpname.trim() ||
        (floorPlanName == "Birch" && fpname == "Carriage House")
      ) {
        return true;
      }
    }
  }

  return false;
};

const FloorplansDisplay = ({
  floorPlans,
  viewing,
  startingPrices,
  endingPrices,
  startingPricesAv,
  endingPricesAv,
  displayAv,
  slug,
  floorplanDeals,
  area,
  citySlug,
  utilitiesIncluded = false,
  communityName,
  email = null,
  soldOutBedCategories = [],
  crossSellingFloorplans = [],
  available = [],
  crossSellPrices = {},
}) => {
  //console.log('floorplandeals',floorplanDeals);
  const vl = viewing.length;
  const scrollableContainerRef = useRef(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);

  const [staticFloorplanDisplay, setStaticFloorplanDisplay] = useState([]);
  const [crossSellDisplay, setCrossSellDisplay] = useState([]);

  const scrollLeft = (event) => {
    //let scrollableContainer = document.getElementById("scrollable-container");
    //scrollableContainer.scrollBy(-50, 0);
    event.stopPropagation(); // Prevent event from bubbling up
    scrollableContainerRef.current.scrollBy(-150, 0);
  };

  const scrollRight = (event) => {
    //let scrollableContainer = document.getElementById("scrollable-container");
    //scrollableContainer.scrollBy(50, 0);
    event.stopPropagation(); // Prevent event from bubbling up
    scrollableContainerRef.current.scrollBy(150, 0);
  };

  const handleScroll = () => {
    const scrollableContainer = scrollableContainerRef.current;
    const leftArrow = leftArrowRef.current;
    const rightArrow = rightArrowRef.current;

    if (scrollableContainer.scrollLeft === 0) {
      leftArrow && (leftArrow.style.display = "none");
    } else {
      leftArrow && (leftArrow.style.display = "block");
    }

    if (
      scrollableContainer.scrollLeft ===
      scrollableContainer.scrollWidth - scrollableContainer.clientWidth
    ) {
      rightArrow && (rightArrow.style.display = "none");
    } else {
      rightArrow && (rightArrow.style.display = "block");
    }
  };

  useEffect(() => {
    if (floorPlans.length > 0) {
      const scrollableContainer = scrollableContainerRef.current;
      if (scrollableContainer) {
        scrollableContainer.addEventListener("scroll", handleScroll);
        handleScroll();
        return () =>
          scrollableContainer.removeEventListener("scroll", handleScroll);
      }
    }
  }, [floorPlans]);

  useEffect(() => {
    // Remove floorplans that we are not currently viewing
    floorPlans = floorPlans.filter((n) =>
      viewing.includes(n.node.floorplanName),
    );

    // build the floorplans
    let staticFloorPlansToDisplay = [];
    floorPlans.map((floorPlan, i) => {
      let avDisplay = displayAv(floorPlan.node.floorplanName);
      let sale = isFloorPlanOnSale(
        floorplanDeals,
        floorPlan.node.floorplanName,
        communityName
      );
      let staticFloorPlanObject = {
        link: `/apartments/${area.toLowerCase()}/${citySlug.toLowerCase()}/${slug.toLowerCase()}/floorplans/${floorPlan.node.floorplanSlug}/`,
        name: floorPlan.node.floorplanName,
        slug: floorPlan.node.floorplanSlug,
        avDisplay: avDisplay,
        sale: sale,
        sqft: floorPlan.node.sqFt
          ? floorPlan.node.sqFt
          : floorPlan.node.sqftRange,
        key:
          String(i) +
          floorPlan.node.floorplanName.replace(/\s+/g, "-").toLowerCase(),
        image: getImage(floorPlan.node.bannerImage),
        beds:
          floorPlan.node.beds != "0"
            ? `${floorPlan.node.beds} bedroom`
            : "Studio",
        bedsCount: floorPlan.node.beds, // Raw beds count for apartment type mapping
        baths: floorPlan.node.numBaths
          ? floorPlan.node.numBaths
          : floorPlan.node.baths,
        startPrice:
          startingPrices &&
          startingPricesAv &&
          findPrice(
            startingPricesAv,
            startingPrices,
            floorPlan.node.floorplanName.trim(),
          ),
        endPrice:
          endingPrices &&
          endingPricesAv &&
          findPrice(
            endingPricesAv,
            endingPrices,
            floorPlan.node.floorplanName.trim(),
            true,
          ),
      };
      staticFloorPlansToDisplay.push(staticFloorPlanObject);
    });
    // Order by avDisplay: "Available Now", "Available Soon", then "Contact Us"
    staticFloorPlansToDisplay.sort((a, b) => {
      const order = ["Available Now", "Available Soon", "Contact Us"];
      return order.indexOf(a.avDisplay) - order.indexOf(b.avDisplay);
    });
    //console.log(staticFloorPlansToDisplay);
    setStaticFloorplanDisplay(staticFloorPlansToDisplay);
  }, [floorPlans, viewing]);

  // Process cross-sell floorplans - show them based on filters or sold-out categories
  useEffect(() => {
    
    // If we have filtered cross-sell floorplans, show them regardless of sold-out status
    if (crossSellingFloorplans && crossSellingFloorplans.length > 0) {
      const crossSellItems = [];
      
      // If there are sold-out categories, prioritize showing those
      if (soldOutBedCategories.length > 0) {
        // For each sold-out bedroom category, find matching cross-sell floorplans
        soldOutBedCategories.forEach(bedCount => {
          const matchingCrossSell = crossSellingFloorplans.filter(fp => {
            const fpBeds = parseInt(fp.beds);
            return fpBeds === bedCount;
          });
          
          // Add ALL matching cross-sell floorplans for each sold-out category
          if (matchingCrossSell.length > 0) {
            matchingCrossSell.forEach(crossSellFp => {
              // Avoid duplicates
              if (!crossSellItems.find(item => item.id === crossSellFp.id)) {
                crossSellItems.push({
                  ...crossSellFp,
                  soldOutBedCategory: bedCount,
                  isCrossSell: true
                });
              }
            });
          }
        });
      } else {
        // No sold-out categories, but we have filtered cross-sell floorplans from filters
        // Show all filtered cross-sell floorplans
        crossSellingFloorplans.forEach(crossSellFp => {
          crossSellItems.push({
            ...crossSellFp,
            isCrossSell: true
          });
        });
      }
      
      setCrossSellDisplay(crossSellItems);
    } else {
      setCrossSellDisplay([]);
    }
  }, [soldOutBedCategories, crossSellingFloorplans]);

  // Combine regular and cross-sell floorplans
  // Separate floorplans by availability status
  const availableNowFloorplans = staticFloorplanDisplay.filter(fp => fp.avDisplay === "Available Now");
  const availableSoonFloorplans = staticFloorplanDisplay.filter(fp => fp.avDisplay === "Available Soon");
  const contactUsFloorplans = staticFloorplanDisplay.filter(fp => fp.avDisplay === "Contact Us");
  
  // Build the display order: Available Now, Available Soon, Cross-Sell, Contact Us
  const allFloorplansToDisplay = [
    ...availableNowFloorplans,
    ...availableSoonFloorplans,
    // Add cross-sell floorplans before "Contact Us" floorplans
    ...crossSellDisplay.map((crossSellFp) => ({
      ...crossSellFp,
      key: `cross-sell-${crossSellFp.id}`,
      isCrossSell: true
    })),
    ...contactUsFloorplans
  ];

  return (
    <div style={{ position: "relative", textAlign: "center" }}>
      {allFloorplansToDisplay.length > 0 && (
        <div
          className="columns is-padded"
          style={{
            textAlign: "center",
            paddingBottom: "2rem",
            paddingTop: "5rem",
            justifyContent: allFloorplansToDisplay.length < 5 ? "center" : "left",
          }}
          id="scrollable-container"
          ref={scrollableContainerRef}
        >
          {allFloorplansToDisplay.map((n, i) => {
            // Determine if we should show arrows (for first and last cards when more than 5 total)
            const showLeftArrow = i === 0 && allFloorplansToDisplay.length > 5;
            const showRightArrow = i === allFloorplansToDisplay.length - 1 && allFloorplansToDisplay.length > 5;
            
            // Check if this is a cross-sell floorplan
            if (n.isCrossSell) {
              // Get actual prices from API data
              const prices = crossSellPrices[n.id];
              const marketPrice = prices ? prices.marketPrice || prices.startPrice : 0;    // Market (actual/discounted)
              const proformaPrice = prices ? prices.proformaPrice || prices.originalPrice : 0; // Proforma (list/original)
              const dealText = prices ? prices.dealText : null; // Deal description if available
              
              return (
                <div key={n.key} style={{ position: "relative", display: "contents" }}>
                  {showLeftArrow && (
                    <span
                      className={`${hoverClickLeft} clickMeArrow`}
                      onClick={(e) => scrollLeft(e)}
                      ref={leftArrowRef}
                    >
                      <img
                        src="/imgs/left-arrow.png"
                        alt="click left"
                        style={{ maxWidth: "50px" }}
                      />
                    </span>
                  )}
                  <CrossSellFloorplanCard
                    floorplan={n}
                    startPrice={marketPrice}      // Pass market price as startPrice
                    endPrice={marketPrice}         // Not used, but kept for consistency
                    originalPrice={proformaPrice}  // Pass proforma price as originalPrice
                    dealText={dealText}            // Pass deal text if available
                    utilitiesIncluded={utilitiesIncluded}
                    avDisplay="Contact Us"
                    isOnSale={proformaPrice > marketPrice}
                    index={i}                      // Pass the index for tracking
                    sourceCommunity={communityName} // Pass the source community name
                  />
                  {showRightArrow && (
                    <span
                      className={`${hoverClick} clickMeArrow`}
                      onClick={(e) => scrollRight(e)}
                      ref={rightArrowRef}
                    >
                      <img
                        src="/imgs/right-arrow.png"
                        alt="click right"
                        style={{ maxWidth: "50px" }}
                      />
                    </span>
                  )}
                </div>
              );
            }
            return (
              <div
                className="column is-one-fifth is-floorplan-column"
                key={n.key}
                onClick={() => navigate(n.link)}
                style={{ maxWidth: "273px" }}
              >
                {showLeftArrow && (
                  <span
                    className={`${hoverClickLeft} clickMeArrow`}
                    onClick={(e) => scrollLeft(e)}
                    ref={leftArrowRef}
                  >
                    <img
                      src="/imgs/left-arrow.png"
                      alt="click left"
                      style={{ maxWidth: "50px" }}
                    />
                  </span>
                )}
                <div
                  className={hoverBig + " card "}
                  style={{ textAlign: "center", height: "100%" }}
                >
                  <div
                    style={{
                      position: "absolute",
                      width: "100%",
                      top: "-2rem",
                    }}
                  >
                    {n.avDisplay != "Contact Us" && n.sale && <FloorplanDeal />}
                  </div>
                  <div
                    className={`${responsivePaddingFloorplanImage} card-image`}
                  >
                    <br />
                    <FloorplanNameTitle
                      floorplanName={n.name}
                      floorplanLink={n.link}
                      isAv={n.avDisplay}
                    />
                    <p
                      className={`${fontColorDark} subtitle is-6 floorplan-subtitle`}
                    >
                      {n.beds}&nbsp;
                      {n.baths} bathroom <br /> {n.sqft} SqFt
                    </p>
                    <figure className="image " style={{ minHeight: "250px" }}>
                      <GatsbyImage image={n.image} alt="Floorplan Image" />
                    </figure>
                  </div>
                  <div className="card-content">
                    <div className="media">
                      <div className="media-content">
                        <PriceDisplay
                          startingPrice={n.startPrice}
                          endingPrice={n.endPrice}
                          utilitiesIncluded={utilitiesIncluded}
                        />
                      </div>
                    </div>
                    <div className="content">
                      <FloorplanLink
                        floorplanLink={n.link}
                        avStatus={n.avDisplay}
                      />
                      <br />
                      <br />
                      <ApplyButton
                        slug={slug}
                        communityName={communityName}
                        floorplanName={n.name}
                        floorplanBeds={n.bedsCount}
                        buttonPosition="floorplans"
                        marginRight="0"
                        isInHeader="false"
                        toEmail={email}
                      />
                      <br />
                      <br />
                    </div>
                  </div>
                </div>
                {showRightArrow && (
                  <span
                    className={`${hoverClick} clickMeArrow`}
                    onClick={(e) => scrollRight(e)}
                    ref={rightArrowRef}
                  >
                    <img
                      src="/imgs/right-arrow.png"
                      alt="click right"
                      style={{ maxWidth: "50px" }}
                    />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FloorplansDisplay;
